import { ZoomTransform, zoomIdentity, ZoomBehavior } from 'd3-zoom';
import { BaseType, Selection } from 'd3-selection';
import isNil from 'lodash/isNil';

const MAX_X = 0;
const MAX_Y = 0;
const MIN_K = 1;

export const isTransfromScaleBelowMin = (t: ZoomTransform) => t.k < MIN_K;

export function getBoundedTransform(
    t: ZoomTransform,
    plotAreaWidth: number | undefined,
    plotAreaHeight: number | undefined
) {
    const boundedK = isTransfromScaleBelowMin(t) ? MIN_K : t.k;

    let boundedX = t.x;
    let boundedY = t.y;

    if (!isNil(plotAreaWidth)) {
        const minX = (1 - boundedK) * plotAreaWidth;
        const isXBelowMin = (currentX: number) => currentX < minX;
        const isXAboveMax = (currentX: number) => currentX > MAX_X;

        boundedX = isXBelowMin(t.x) ? minX : t.x;
        boundedX = isXAboveMax(boundedX) ? MAX_X : boundedX;
    }

    if (!isNil(plotAreaHeight)) {
        const minY = (1 - boundedK) * plotAreaHeight;
        const isYBelowMin = (currentY: number) => currentY < minY;
        const isYAboveMax = (currentY: number) => currentY > MAX_Y;

        boundedY = isYBelowMin(t.y) ? minY : t.y;
        boundedY = isYAboveMax(boundedY) ? MAX_Y : boundedY;
    }

    return zoomIdentity.translate(boundedX, boundedY).scale(boundedK);
}

export function computeZoomedTransform(
    t: ZoomTransform,
    scale: number,
    plotAreaWidth: number | undefined,
    plotAreaHeight: number | undefined
) {
    const newX = isNil(plotAreaWidth)
        ? t.x
        : scale * t.x + (plotAreaWidth / 2) * (1 - scale);
    const newY = isNil(plotAreaHeight)
        ? t.y
        : scale * t.y + (plotAreaHeight / 2) * (1 - scale);

    return zoomIdentity.translate(newX, newY).scale(scale * t.k);
}

function isZoomInEvent(start: ZoomTransform, end: ZoomTransform) {
    return end.k > start.k;
}

function isZoomOutEvent(start: ZoomTransform, end: ZoomTransform) {
    return end.k < start.k;
}

export interface ZoomControl {
    resetZoom: () => void;
}

export function processZoomEvents(
    start: ZoomTransform,
    end: ZoomTransform,
    onZoomIn: ((c: ZoomControl) => void) | undefined,
    onZoomOut: ((c: ZoomControl) => void) | undefined
) {
    let result = end;
    const resetZoom = () => {
        result = zoomIdentity;
    };

    const control = {
        resetZoom,
    };

    if (onZoomIn && isZoomInEvent(start, end)) {
        onZoomIn(control);
    }

    if (onZoomOut && isZoomOutEvent(start, end)) {
        onZoomOut(control);
    }

    return result;
}

export function isEqualTransform(zt1: ZoomTransform, zt2: ZoomTransform) {
    return zt1.k === zt2.k && zt1.x === zt2.x && zt1.y === zt2.y;
}

export type EventValueFn<T extends BaseType, Datum, Result> = (
    this: T,
    event: unknown,
    datum: Datum
) => Result;

type ZoomListener = EventValueFn<Element, unknown, void>;
interface ListenersResult {
    start?: ZoomListener;
    zoom?: ZoomListener;
    end?: ZoomListener;
}

export function applyZoomTransformToElement(
    plotAreaSelection: Selection<Element, unknown, null, undefined>,
    zoomObj: ZoomBehavior<Element, unknown>,
    transform: ZoomTransform
) {
    const typeNames: (keyof ListenersResult)[] = ['start', 'zoom', 'end'];
    const zoomListeners = typeNames
        .map((x) => ({
            typeName: x,
            listener: zoomObj.on(x),
        }))
        .reduce<ListenersResult>(
            (res, val) => ({ ...res, [val.typeName]: val.listener }),
            {}
        );

    // Remove listeners to avoid ZoomContext transform being reset.
    // Specifically avoids multiple charts sending update events in random order
    typeNames.forEach((typeName) => {
        zoomObj.on(typeName, null);
    });

    // Apply transform to Element. This generates typeName events, which is why the listeners above were removed.
    plotAreaSelection.call(zoomObj.transform, transform);

    // reinstate listeners
    typeNames.forEach((typeName) => {
        const listener = zoomListeners[typeName];
        if (listener) {
            zoomObj.on(typeName, listener);
        } else {
            zoomObj.on(typeName, null);
        }
    });
}
